import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, IconButton, Grid2, Button, useMediaQuery, useTheme, } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from "react-hot-toast";
import { useWindowResize } from '../../hooks/useWindowResize';
import { useGet } from '../../hooks/useApi';
import { useAddCourseFavorite } from '../../hooks/useAddCourseFavorite';
import ReusableDrawer from '../CheckoutSidebar/ReusableDrawer';
import TabPanel from '../CheckoutSidebar/TabPanel';

const ItemDetails = ({ 
  buttontext, 
  detail, 
  position = "same-line", 
  category_title = "", 
  items, 
  item_details = [{ label: '', value: '' }] 
}) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [openCourseDrawer, setOpenCourseDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [courseFavorites, setCourseFavorites] = useState([]);
  const { id } = useParams();
  const customerId = sessionStorage.getItem("userId");
  const { data: getCourseFavorite } = useGet(`/api/Courses/favorites/${customerId}`);
  const favoriteCourseCard = getCourseFavorite?.data;
  const { addToFavorite } = useAddCourseFavorite();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    const savedBookmarks = JSON.parse(localStorage.getItem('bookmarked-courses') || '[]');
    setCourseFavorites(savedBookmarks);
  }, [items.id]);

  useEffect(() => {
    if (favoriteCourseCard) setCourseFavorites(favoriteCourseCard);
  }, [favoriteCourseCard]);

  const handleAddCourseToFavorite = async (id) => {
    const isAlreadyFavorite = courseFavorites?.some(fav => Number(fav.courseId) === Number(id));
    if (isAlreadyFavorite) return toast.error('Already in favorites!', { position: "top-right" });
    try {
      await addToFavorite(id, customerId);
      setCourseFavorites(prev => [...prev, { courseId: id }]);
      toast.success('Added to favorites successfully!', { position: "top-right" });
    } catch (err) {
      toast.error('Failed to add to favorites', { position: "top-right" });
    }
  };

  const handleCheckoutCourse = () => {
    setDrawerType('course');
    setOpenCourseDrawer(true);
    const isOnCheckoutForm = location.pathname === '/product/course-details/checkout-form';
    const isOnCourseDetails = location.pathname === `/product/course-details/${id}`;

    if (isOnCheckoutForm && !isMobile) navigate(`/product/course-details/${id}`);
    if (isOnCourseDetails && isMobile) navigate(`/product/course-details/checkout-form?productId=${id}`);
  };

  const handleDrawerCourseClose = () => {
    setOpenCourseDrawer(false);
    setDrawerType(null);
  };
    // Handle thumbnail click
    const handleThumbnailClick = (index) => {
        setSelectedImage(index);
      };
    
      
      // Handle next image
    const handleNextImage = () => {
      if (items?.images?.length > 0) {
        setSelectedImage((prev) => (prev + 1) % items.images.length); // Cycle through images
      }
    };
    
    // Handle previous image
    
const handlePrevImage = () => {
    if (items?.images?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + items.images.length) % items.images.length); // Cycle through images
    }
  };
  

  const handleBreakpointChange = useCallback((newBreakpoint, width) => {
    const isCheckoutForm = location.pathname === '/product/course-details/checkout-form';
    const isCourseDetails = location.pathname === `/product/course-details/${id}`;

    if (newBreakpoint === 'mobile' && isCourseDetails) {
      navigate(`/product/course-details/${id}`);
    } else if (newBreakpoint === 'desktop' && isCheckoutForm) {
      navigate(`/product/course-details/${id}`);
    }
  }, [location.pathname, id, navigate]);

  const { isMobile } = useWindowResize(handleBreakpointChange);

  const positionStyles = position === "same-line"
    ? { display: "flex", justifyContent: "space-between", alignItems: "center" }
    : { position: "absolute", top: "10px", left: { md: "15px", xs: '0px' }, right: "0", display: "flex", justifyContent: "space-between", alignItems: "center" };

  return (

    <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 4, padding: { md: 2, xs: 1 }, fontFamily: theme => theme.typography.smallText }}>
    {/* Main Image Section */}
    <Box sx={{ position: 'relative', flex: 1, width: { xs: "100%", md: '50%' } }}>
      <Box sx={{ position: 'relative' }}>
        {/* Arrow buttons */}
        <IconButton
          sx={{
            position: 'absolute', top: '50%', left: 30, transform: 'translateY(-50%)',
            backgroundColor: '#EEE6FE', borderRadius: '50%', '&:hover': {
              color: '#fff',
              backgroundColor: theme => theme.palette.secondary.main,
              '& svg': {
                fill: '#fff', // Change the color of the SVG on hover
              }
            },
          }}
          onClick={handlePrevImage}
        >
          <ChevronLeftIcon sx={{
            fill: '#5604F6', // Default color of the SVG
            transition: 'fill 0.3s ease', // Smooth transition on hover
            '&:hover': {
              fill: '#fff', // Fill color when hovered
            }
          }} />
        </IconButton>

        <img
          src={items?.images?.[selectedImage]?.imagepath}
          alt={items?.name}
          style={{
            width: '100%', height: '450px', borderRadius: 8, objectFit: 'cover'
          }}
        />

        <IconButton
          sx={{
            position: 'absolute', top: '50%', right: 30, transform: 'translateY(-50%)',
            backgroundColor: '#EEE6FE', borderRadius: '50%', '&:hover': {
              color: '#fff',
              backgroundColor: theme => theme.palette.secondary.main,
              '& svg': {
                fill: '#fff', // Change the color of the SVG on hover
              }
            },
          }}
          onClick={handleNextImage}
        >
          <ChevronRightIcon sx={{
            fill: '#5604F6', // Default color of the SVG
            transition: 'fill 0.3s ease', // Smooth transition on hover
            '&:hover': {
              fill: '#fff', // Fill color when hovered
            }
          }} />
        </IconButton>
      </Box>

      {/* Thumbnail Images */}
      <Grid2 container spacing={2} justifyContent={'space-between'} sx={{ marginTop: 4 }}>
        {items?.images?.map((image, index) => (
          <Grid2 item xs={4} key={image?.id} size={{ xs: 4, md: 4 }}>
            <img
              src={image?.imagepath}
              alt={`Thumbnail ${image?.id + 1}`}
              loading="lazy"
              style={{
                cursor: 'pointer',
                width: '100%',
                height: '100%',
                borderRadius: 8,
                border: selectedImage === image?.id ? '2px solid #5604F6' : 'none',
              }}
              onClick={() => handleThumbnailClick(image?.id)}
            />
          </Grid2>
        ))}
      </Grid2>

    </Box>
    <Box position='relative' sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius:'15px', backgroundColor:'#fff', padding: {md: 4, xs: 1}}}>
      {items?.manufacturer && (
        <Typography
          sx={{
            backgroundColor: 'rgba(246, 167, 4, 1)',
            borderRadius: '8px',
            px: 1,
            textTransform: 'uppercase',
            width: 'fit-content',
            height: 30,
            gap: 1,
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            my: {md: 2, xs: 1},
          }}
        >
          {items.manufacturer?.image && (
            <img
              src={items?.manufacturer?.image}
              alt="Manufacturer img"
              style={{ borderRadius: '50%', width: 16, height: 16 }}
            />
          )}
          {items.manufacturer?.name}
        </Typography>
      )}

      <Typography variant="h5" sx={{ fontWeight: 'bold', color:'#5604F6', marginTop: position === "top" ? {md: 6, xs: 8} : 0 }}>
        {items?.name}
      </Typography>

      <Typography variant="body1" sx={{ marginTop: 1 }}>
        {items?.longDescription}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop:position === 'top' ? 0 : 4, ...positionStyles }}>
        <Typography variant="body1" sx={{ margin: 1 }}>
          {category_title}
        </Typography>

        {items?.catgoryName && (
          <Typography
            sx={{
              backgroundColor: 'rgba(246, 167, 4, 1)',
              borderRadius: '8px',
              px: 1,
              textTransform: 'uppercase',
              width: 'fit-content',
              height: 'auto',
              gap: 1,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              margin: {md: 2, xs: 1}
            }}
          >
            {items?.category_image && (
              <img
                src={items?.category_image}
                alt="Category img"
                style={{ borderRadius: '50%', width: 16, height: 16 }}
              />
            )}
            {items?.catgoryName}
          </Typography>
        )}
      </Box>

      <Box sx={{ display:'flex', flexDirection:'column', backgroundColor:'#E7E7E733', borderRadius:'10px', padding: {md: 2, xs: 1}, marginTop:2 }}>
        <Box sx={{ display:'flex', justifyContent:'flex-start', alignItems:'center', gap: 1 }}>
          <Typography variant="h6" sx={{ color: theme => theme.palette.primary.main, fontSize: 20 }}>
            ₦ {items?.price}
          </Typography>
          <Typography variant="body2" sx={{ color:'#12121299', fontSize: 12 }}>
            (Per Unit)
          </Typography>
        </Box>
      </Box>

      <Box sx={{ border: '1px solid #D2D1D6', borderRadius: '10px', padding: 2, marginTop: 4 }}>
        <Typography variant="body2" sx={{ marginTop: 1 }}>
          {detail}
        </Typography>

        {item_details.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: index < item_details.length - 1 ? '1px solid #D2D1D6' : 'none',
              marginTop: 1,
              paddingBottom: 0.5
            }}
          >
            <Typography variant="body2" sx={{ color: '#121212CC', fontSize: 16 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#121212CC', fontSize: 16, textTransform: 'capitalize', fontWeight:'600' }}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display:'flex', flex: 1, alignItems:'center', justifyContent: 'space-between', marginTop: {md: 4, xs: 2}, marginBottom: {md: 0, xs: 2} }}>
        {pathname.includes('course-details') && (
          <Box sx={{ marginTop: 2, width: 'auto' }}>
            <Button
              variant="contained"
              onClick={() => handleAddCourseToFavorite(id)}
              color="secondary"
              sx={{
                width: '100%',
                padding: {md: '10px 20px', xs: '5px 8px'},
                borderRadius: '25px',
                textTransform:'capitalize',
                backgroundColor: '#F1F0F2',
                color:'#6E6B7B',
              }}
            >
              {buttontext}
            </Button>
          </Box>
        )}

        <Box sx={{ marginTop: 2, width: 145 }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleCheckoutCourse}
            sx={{
              width: '100%',
              padding: { md: '10px 20px', xs: '5px 8px' },
              borderRadius: '25px',
              textTransform: 'capitalize',
              backgroundColor: '#5604F6',
              color: '#fff',
            }}
          >
            Order Now
          </Button>
        </Box>
      </Box>

    </Box>
    
    <Toaster />

<ReusableDrawer
  state={openCourseDrawer}
  drawerType={drawerType}
  courseState={openCourseDrawer}
  onClose={handleDrawerCourseClose}
  TabPanel={TabPanel}
  setOnClose={setOpenCourseDrawer}
  title="Checkout"
  width="35%"
/>
    </Box>
  );
};

export default ItemDetails;
